<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Button, Spinner, Tabs, useToast } from '@drama/ui'

import { createNovelComparison } from '../../api/novel/comparisons.js'
import { getNovelRun, retryNovelJob } from '../../api/novel/runs.js'
import AuditBar from '../../components/novel/audit/AuditBar.vue'
import AuditBaseline from '../../components/novel/audit/AuditBaseline.vue'
import AuditCalls from '../../components/novel/audit/AuditCalls.vue'
import AuditCharacters from '../../components/novel/audit/AuditCharacters.vue'
import AuditDirector from '../../components/novel/audit/AuditDirector.vue'
import AuditDrama from '../../components/novel/audit/AuditDrama.vue'
import AuditEvents from '../../components/novel/audit/AuditEvents.vue'
import AuditHistory from '../../components/novel/audit/AuditHistory.vue'
import AuditOverview from '../../components/novel/audit/AuditOverview.vue'
import { useAuditText } from '../../components/novel/audit/audit-text.js'
import { asArray, branchRequests, evidenceSources, indexCalls, modelStages, preferenceHash } from '../../components/novel/audit/run-observer.js'
import { useNovelPageText } from '../../components/novel/novel-page-text.js'
import '../../components/novel/audit/audit.css'

const TAB_KEYS = ['overview', 'characters', 'timeline', 'director', 'story', 'calls', 'drama', 'raw']

const props = defineProps({ runId: { type: String, required: true } })

const { t, tr } = useAuditText()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const { apiError, apiErrorLine } = useNovelPageText()

const loading = ref(false)
const error = ref(null)
const detail = ref(null)
const loadedAt = ref(null)
const retrying = ref('')
const comparing = ref(false)
const compareError = ref(null)
const branchId = ref(typeof route.query.branch_id === 'string' ? route.query.branch_id : '')
const characterId = ref('')
// 分支偏好哈希的计算状态：pending 计算中，ready 已得到 hash，failed 算不出来（reason 是原因）。
const branchPreference = ref({ status: 'pending', hash: null, reason: '' })
let pollTimer = null
let activeLoad = null
let loadVersion = 0
let disposed = false

const run = computed(() => detail.value?.run ?? null)
const snapshot = computed(() => detail.value?.snapshot ?? {})
const pack = computed(() => detail.value?.pack ?? {})
const calls = computed(() => detail.value?.calls ?? [])
const branch = computed(() => asArray(snapshot.value.branches).find((item) => item.branch_id === branchId.value) ?? null)
const requests = computed(() => (branch.value ? branchRequests(snapshot.value, branch.value.branch_id) : []))
const latestRequest = computed(() => requests.value.at(-1) ?? null)
const callsById = computed(() => indexCalls(calls.value))
const stages = computed(() => (latestRequest.value ? modelStages(latestRequest.value.audits, callsById.value) : []))
const sources = computed(() => (branch.value ? evidenceSources(pack.value, branch.value) : new Map()))
const failedJobs = computed(() => asArray(branch.value?.jobs).filter((job) => job.status === 'failed'))

const tabs = computed(() => TAB_KEYS.map((key) => ({ key, label: tr(`tabs.${key}`) })))
// 当前页签写在 URL hash 里，刷新或转发链接都回到同一页签。
const tab = computed({
  get: () => {
    const key = route.hash.slice(1)
    return TAB_KEYS.includes(key) ? key : 'overview'
  },
  set: (key) => { router.replace({ query: route.query, hash: `#${key}` }) },
})

onMounted(() => {
  void load()
  document.addEventListener('visibilitychange', onVisibility)
})
onBeforeUnmount(() => {
  disposed = true
  loadVersion += 1
  window.clearTimeout(pollTimer)
  document.removeEventListener('visibilitychange', onVisibility)
})
watch(() => props.runId, () => {
  loadVersion += 1
  activeLoad = null
  window.clearTimeout(pollTimer)
  detail.value = null
  void load()
})
watch(() => route.query.branch_id, (value) => {
  const known = asArray(snapshot.value.branches).map((item) => item.branch_id)
  branchId.value = typeof value === 'string' && known.includes(value)
    ? value : snapshot.value.active_branch_id ?? known[0] ?? ''
})
// 导演建议是否可用要比对分支偏好的 SHA-256（引擎 director_preference_hash），浏览器里只能异步算；切换分支后丢弃旧分支的结果。
// crypto.subtle 只在 HTTPS 或 localhost 下可用，算不出时导演状态显示"无法计算"。
watch(branch, async (value) => {
  branchPreference.value = { status: 'pending', hash: null, reason: '' }
  if (!value) return
  try {
    const hash = await preferenceHash(value)
    if (branch.value === value) branchPreference.value = { status: 'ready', hash, reason: '' }
  } catch (hashError) {
    if (branch.value === value) branchPreference.value = { status: 'failed', hash: null, reason: hashError.message }
  }
}, { immediate: true })

function schedulePoll() {
  window.clearTimeout(pollTimer)
  if (disposed || document.hidden) return
  pollTimer = window.setTimeout(() => void load({ quiet: true }), 3000)
}

function onVisibility() {
  window.clearTimeout(pollTimer)
  if (!document.hidden) void load({ quiet: true })
}

function load({ quiet = false } = {}) {
  if (disposed) return Promise.resolve()
  if (activeLoad) return activeLoad
  const version = ++loadVersion
  if (!quiet) loading.value = true
  error.value = null
  const pending = (async () => {
    try {
      const next = await getNovelRun(props.runId)
      if (disposed || version !== loadVersion) return
      detail.value = next
      loadedAt.value = new Date()
      const known = asArray(next.snapshot.branches).map((item) => item.branch_id)
      if (!known.includes(branchId.value)) branchId.value = next.snapshot.active_branch_id ?? known[0] ?? ''
    } catch (requestError) {
      if (!disposed && version === loadVersion) error.value = apiError(requestError)
    } finally {
      if (version === loadVersion) {
        loading.value = false
        activeLoad = null
        schedulePoll()
      }
    }
  })()
  activeLoad = pending
  return pending
}

/** 从这一局的当前进度建模型对比并跳到对比页；建不成（正在生成、作品无正文、模型未配置）用页面级错误条显示。 */
async function compare() {
  comparing.value = true
  compareError.value = null
  try {
    const created = await createNovelComparison(props.runId)
    await router.push({ name: 'novel-comparison', params: { comparisonId: created.comparison_id } })
  } catch (requestError) {
    compareError.value = apiError(requestError)
  } finally {
    comparing.value = false
  }
}

/** 逐个重试失败任务，出错即停；无论成败都重新拉一次详情，让已排队的任务状态落到页面上。 */
async function retry(jobs, marker) {
  retrying.value = marker
  try {
    for (const job of jobs) await retryNovelJob(props.runId, job.job_id)
    toast.success(tr('jobs.retried', { count: jobs.length }))
  } catch (requestError) {
    toast.error(apiErrorLine(requestError), { duration: 0 })
  } finally {
    retrying.value = ''
  }
  await load()
}
</script>

<template>
  <div class="vga-page au" data-insp-path="apps/visual-game-admin/src/pages/novel/RunAudit.vue" data-testid="run-observer">
    <div v-if="loading && !detail" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error && !detail" class="vga-state">
      <p class="vga-error" :title="error.title">{{ error.text }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else-if="detail && branch">
      <p v-if="error" class="vga-error" role="alert" :title="error.title">{{ error.text }}</p>
      <AuditBar
        :run="run"
        :snapshot="snapshot"
        :branch="branch"
        :requests="requests"
        :loading="loading"
        :loaded-at="loadedAt"
        :failed-jobs="failedJobs"
        :retrying="retrying"
        :comparing="comparing"
        @refresh="load"
        @retry="retry($event, 'all')"
        @compare="compare"
      />
      <p v-if="compareError" class="vga-error" role="alert" :title="compareError.title">{{ compareError.text }}</p>
      <Tabs v-model="tab" :tabs="tabs">
        <template #overview>
          <AuditOverview v-model:branchId="branchId" :run="run" :snapshot="snapshot" :pack="pack" :branch="branch" :request="latestRequest" :stages="stages" />
        </template>
        <template #characters>
          <AuditCharacters v-model:characterId="characterId" :pack="pack" :branch="branch" :sources="sources" />
        </template>
        <template #timeline>
          <AuditEvents :pack="pack" :branch="branch" :sources="sources" />
        </template>
        <template #director>
          <AuditDirector :snapshot="snapshot" :pack="pack" :branch="branch" :sources="sources" :preference="branchPreference" />
        </template>
        <template #story>
          <AuditHistory :snapshot="snapshot" :pack="pack" :branch="branch" :requests="requests" />
        </template>
        <template #calls>
          <AuditCalls :snapshot="snapshot" :branch="branch" :calls="calls" :retrying="retrying" @retry="retry([$event], $event.job_id)" />
        </template>
        <template #drama>
          <AuditDrama :sessions="detail.drama_sessions" :pack="pack" />
        </template>
        <template #raw>
          <AuditBaseline :snapshot="snapshot" :pack="pack" />
        </template>
      </Tabs>
    </template>
    <p v-else-if="detail" class="vga-state">{{ tr('empty') }}</p>
  </div>
</template>
